import { useCallback } from 'react'
import { camelizeKeys, decamelizeKeys } from 'humps'

import { useApiOrigin } from '@/utils/env'
import { useAuthorizationHeader } from '@/utils/auth'
import { useLoading } from '@/utils/loading'
import { useToast } from '@/utils/toast'
import { ResponseError, useGetErrorMessage } from '@/utils/error'

// show loading after debounce time
const LOADING_DEBOUNCE = 300

type RequestOptions<REQ> = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  data?: REQ
  showToast?: boolean
  showLoading?: boolean
}

type StringRecord = Record<string, string | number>

function stringifyDataValue(data: StringRecord) {
  const stringifyData: Record<string, string> = {}
  Object.keys(data).forEach((key) => {
    stringifyData[key] = String(data[key])
  })
  return stringifyData
}

export function useRequest() {
  const apiBase = useApiOrigin()
  const authorizationHeader = useAuthorizationHeader()
  const toast = useToast()
  const [showLoading, hideLoading] = useLoading()
  const getErrorMessage = useGetErrorMessage()

  return useCallback(
    async function request<RES, REQ = any>(
      pathname: string,
      inputOptions: RequestOptions<REQ> = {}
    ) {
      // fetch options
      const options: RequestOptions<REQ> = {
        showToast: true,
        showLoading: true,
        ...inputOptions,
      }

      // fetch endpoint
      const inputPathname = pathname.replace(/^\//, '')
      const input = new URL(inputPathname, apiBase)

      // http headers
      const method = options.method || 'GET'

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(await authorizationHeader()),
      }
      // NGROKを使っている場合は、ngrok-skip-browser-warningヘッダーを追加する
      if (apiBase?.includes('ngrok-free.app')) {
        headers['ngrok-skip-browser-warning'] = 'true'
      }
      const init: RequestInit = { method, headers }

      // fetch body
      if (['POST', 'PUT'].includes(method)) {
        const data = options.data as unknown as object
        init.body = JSON.stringify(decamelizeKeys(data))
      } else {
        const data = (options.data || {}) as unknown as object
        const stringifyData = stringifyDataValue(
          decamelizeKeys(data) as StringRecord
        )
        input.search = new URLSearchParams(stringifyData).toString()
      }

      // loading before fetch
      let loadingTimeout = null
      let loadingTask = null
      if (options.showLoading) {
        loadingTimeout = window.setTimeout(() => {
          loadingTask = showLoading()
        }, LOADING_DEBOUNCE)
      }

      // fetch
      const fetchResponse = await window.fetch(input.toString(), init)

      // loading after fetch
      if (options.showLoading) {
        if (loadingTimeout) {
          window.clearTimeout(loadingTimeout)
        }
        if (loadingTask) {
          hideLoading(loadingTask)
        }
      }

      // 200
      // 注: 戻り値型は RES のままなので、204 を返すエンドポイントの呼び出し側は型上
      // 非 null を仮定したまま null を受け取る（従来は空ボディの json() が即 throw していた）。
      // 現状の消費者は戻り値未使用の DELETE のみ。戻り値を使う消費者が増えたら RES | null 化を検討する。
      if (fetchResponse.ok) {
        // 204 No Content は成功として null(無条件 json() は空ボディで throw するため)。
        if (fetchResponse.status === 204) return null as unknown as RES
        const responseBody = (await fetchResponse.json()) as object
        return camelizeKeys(responseBody) as unknown as RES
      }

      // not 200 with valid body
      const fetchResponseBody = (await fetchResponse
        .json()
        .catch(() => null)) as ResponseError | null

      if (fetchResponseBody) {
        console.error('Request failed:', {
          url: input.toString(),
          method,
          status: fetchResponse.status,
          error: fetchResponseBody,
        })

        if (options.showToast) {
          const message = getErrorMessage(fetchResponseBody)
          toast(message, 'error')
        }

        return Promise.reject(fetchResponseBody)
      }

      console.error('Request failed with invalid response:', {
        url: input.toString(),
        method,
        status: fetchResponse.status,
      })

      return Promise.reject(fetchResponse)
    },
    [
      apiBase,
      showLoading,
      hideLoading,
      toast,
      getErrorMessage,
      authorizationHeader,
    ]
  )
}
